import React from 'react';
import Popover from '@mui/material/Popover';
import { Button, Box, Typography, IconButton } from '@mui/material';
import { useAppDispatch } from 'common/hook';
import FormProvider from 'common/components/form-provider';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import BaseDatePicker from 'common/components/base-date-picker';
import { useTranslation } from 'react-i18next';
import { createStyleSheet } from 'utils/helpers';
import SyncIcon from '@mui/icons-material/Sync';
import CampaignConstants from 'pages/admin/campaign/constants';
import {
    clearOverviewSearchForm,
    setOverviewSearchForm,
} from 'slices/overviewSlice';
import { overViewFilterSchema } from 'common/schema';

interface IOverviewFilterProps {
    open: boolean;
    headerFilterId: string | undefined;
    anchorEl: HTMLButtonElement | null;
    handleClose: () => void;
}

const OverviewFilter: React.FC<IOverviewFilterProps> = ({
    open,
    headerFilterId,
    anchorEl,
    handleClose,
}) => {
    const dispatch = useAppDispatch();

    const { t } = useTranslation();
    const methods = useForm({
        resolver: yupResolver(overViewFilterSchema),
        defaultValues: {
            status: [CampaignConstants.status.ALL],
            startDate: '',
            endDate: '',
        },
    });

    const { handleSubmit, reset, watch, setValue } = methods;
    const onSubmit = handleSubmit(async (data) => {
        const params = { ...data };
        dispatch(setOverviewSearchForm(params));
        handleClose();

        return;
    });

    const handleRenewForm = () => {
        dispatch(clearOverviewSearchForm());
        reset({
            status: [],
            region: [],
            device: [],
            startDate: '',
            endDate: '',
        });
    };

    const topPixel = 104;

    const datePickerStyle = {
        '& .MuiInputBase-root': {
            height: '29px',
            borderRadius: '10px',
        },
        '& .MuiIconButton-root svg': {
            width: '14px',
            height: '14px',
        },
    };

    const checkBoxStyle = {
        '& .MuiFormControlLabel-label': {
            fontSize: '20px',
        },
    };

    const groupTitleStyle = {
        fontWeight: 600,
        fontSize: '24px',
        lineHeight: '29px',
        marginBottom: '15px',
    };

    const startDate = watch('startDate');
    const endDate = watch('endDate');
    React.useEffect(() => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (start > end) {
                setValue('endDate', '');
            }
        }
    }, [startDate, endDate, setValue]);

    return (
        <Popover
            id={headerFilterId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            sx={{
                '& .MuiPaper-root.MuiPopover-paper': {
                    top: `${topPixel}px !important`,
                    maxHeight: `min(906px, calc(100% - ${topPixel}px))`,
                    borderRadius: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box
                    sx={{
                        padding: '20px 30px 85px',
                        width: '430px',
                        overflowY: 'auto',
                        maxHeight: '700px',
                    }}
                >
                    <Box
                        mb={'20px'}
                        display={'flex'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                    >
                        <Typography
                            sx={{
                                color: (theme) => theme.palette.primary.main,
                                fontWeight: 800,
                                fontSize: '27px',
                                lineHeight: '33px',
                            }}
                        >
                            {t('layout.header.filter.title')}
                        </Typography>
                        <IconButton
                            onClick={handleRenewForm}
                            aria-label="refresh"
                        >
                            <SyncIcon
                                sx={{
                                    fontSize: 32,
                                    color: 'var(--color-primary)',
                                }}
                            />
                        </IconButton>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Thời gian
                            </Typography>
                        </Box>
                        <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            gap={1}
                        >
                            {t('layout.header.filter.time.start')}
                            <BaseDatePicker
                                name="startDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                            {t('layout.header.filter.time.end')}
                            <BaseDatePicker
                                name="endDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                        </Box>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Trạng thái
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="status"
                                options={
                                    Object.keys(CampaignConstants.status).map(
                                        (status: string) => ({
                                            label: t(
                                                `page.campaign.status.${status.toLowerCase()}`
                                            ),
                                            value: CampaignConstants.status[
                                                status as keyof typeof CampaignConstants.status
                                            ],
                                        })
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>

                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Thiết bị
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="device"
                                options={
                                    [
                                        {
                                            label: 'Mobile',
                                            value: '0',
                                        },
                                        {
                                            label: 'Desktop',
                                            value: '1',
                                        },
                                    ] as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Khu vực
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="region"
                                options={
                                    [
                                        {
                                            label: 'Hà Nội',
                                            value: '0',
                                        },
                                        {
                                            label: 'Hồ Chí Minh',
                                            value: '1',
                                        },
                                    ] as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box
                    display={'flex'}
                    justifyContent={'end'}
                    gap={2}
                    padding={'20px 30px'}
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        backgroundColor: '#fff',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        style={styles.modalButton}
                    >
                        Đóng
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        aria-label="filter"
                        onClick={onSubmit}
                        style={styles.modalButton}
                    >
                        Lọc
                    </Button>
                </Box>
            </FormProvider>
        </Popover>
    );
};

const styles = createStyleSheet({
    modalButton: {
        textTransform: 'none',
        borderRadius: '40px',
        minHeight: '42px',
        minWidth: '84px',
        fontSize: '14px',
        fontWeight: '600',
        borderWidth: '2px',
    },
});

export default OverviewFilter;
